// Edit an existing manual upload (default-on brand-level setting
// `ads_express_manual_upload`, same admin opt-out as the upload dialog). Lets
// the user retag persona / product / narrative angle and/or replace the
// uploaded image on a source="manual_upload" row after the fact. Uses the
// SAME brand-scoped PickerSelect option sources as ManualAdUpload (narrative
// angles, archived excluded, narrowed to the chosen persona/product); the
// server validates ids and re-snapshots names — client names never trusted.
import { useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Loader2, Package, Sparkles, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PickerSelect, type PickerGroup, type PickerOption } from "@/components/picker-select";
import { groupAnglesByFamily } from "@/lib/angle-groups";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { invalidateApprovalStates } from "@/lib/approval-states";
import type { Persona, Product, Offer, NarrativeAngle, GeneratedAd } from "@shared/schema";

type EditableAd = Pick<
  GeneratedAd,
  "id" | "brandId" | "personaId" | "productId" | "offerId" | "creativeAngleId" | "customName"
> & { mediaUrl?: string | null; durableUrl?: string | null };

export function ManualAdEditDialog({
  ad,
  personas,
  products,
  offers = [],
  angles,
  onClose,
  onSaved,
}: {
  ad: EditableAd;
  personas: Persona[];
  products: Product[];
  offers?: Offer[];
  angles: NarrativeAngle[];
  onClose: () => void;
  // Called after a successful save so the caller can refresh the listing.
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [personaId, setPersonaId] = useState(ad.personaId ?? "");
  const [productId, setProductId] = useState(ad.productId ?? "");
  const [offerId, setOfferId] = useState(ad.offerId ?? "");
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offer">(ad.offerId ? "offer" : "product");
  const [angleId, setAngleId] = useState(ad.creativeAngleId ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  // ── Ads Express-parity option lists (same as ManualAdUpload) ─────────────
  const personaOptions = useMemo<PickerOption[]>(
    () =>
      personas.map((p) => ({
        value: p.id,
        label: p.name,
        imageUrl: (p as { imageUrl?: string | null }).imageUrl ?? null,
      })),
    [personas],
  );
  const productOptions = useMemo<PickerOption[]>(
    () =>
      [...products]
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
        .map((p) => {
          const media = (p as { mediaJson?: string[] | null }).mediaJson;
          const first = Array.isArray(media) && media.length > 0 ? media[0] : null;
          return { value: p.id, label: p.name, imageUrl: first };
        }),
    [products],
  );
  const offerOptions = useMemo<PickerOption[]>(
    () => [...offers].sort((a, b) => a.name.localeCompare(b.name)).map((o) => ({ value: o.id, label: o.name })),
    [offers],
  );
  const visibleAngles = useMemo(
    () =>
      angles.filter(
        (a) =>
          a.status !== "archived" &&
          (!personaId || a.personaId === personaId) &&
          (!productId || a.productId === productId) &&
          (!offerId || a.offeringId === offerId),
      ),
    [angles, personaId, productId, offerId],
  );
  const angleGroups = useMemo<PickerGroup[]>(
    () =>
      groupAnglesByFamily(visibleAngles).map((g) => ({
        label: g.label,
        options: g.angles.map((a) => ({
          value: a.id,
          label: a.name,
          description: a.summary ?? null,
        })),
      })),
    [visibleAngles],
  );
  // Keep the chosen angle consistent with the persona/product narrowing
  // (never quietly submit a mismatched angleId — Ads Express behavior).
  useEffect(() => {
    if (angleId && !visibleAngles.some((a) => a.id === angleId)) setAngleId("");
  }, [angleId, visibleAngles]);

  const currentImage = previewUrl ?? ad.durableUrl ?? ad.mediaUrl ?? null;

  const handleSave = async () => {
    setSaving(true);
    try {
      // Tags first — omitted keys unchanged; "" → explicit null clears.
      const tagsRes = await fetch(`/api/generated-ads/${ad.id}/manual-tags`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          personaId: personaId || null,
          productId: productId || null,
          offerId: offerId || null,
          angleId: angleId || null,
        }),
      });
      if (!tagsRes.ok) {
        const d = await tagsRes.json().catch(() => ({}));
        throw new Error(d?.message || `Tag update failed (${tagsRes.status})`);
      }
      if (file) {
        const fd = new FormData();
        fd.append("image", file);
        const imgRes = await fetch(`/api/generated-ads/${ad.id}/manual-image`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!imgRes.ok) {
          const d = await imgRes.json().catch(() => ({}));
          throw new Error(d?.message || `Image replacement failed (${imgRes.status})`);
        }
        invalidateApprovalStates(queryClient, ad.brandId);
      }
      toast({ title: "Manual upload updated" });
      onSaved();
      onClose();
    } catch (e: unknown) {
      toast({
        title: "Couldn't update",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogTitle>Edit manual upload</DialogTitle>
        <div className="space-y-3">
          {/* Replace image */}
          <div className="flex items-center gap-3">
            {currentImage && (
              <img
                src={currentImage}
                alt=""
                className="h-16 w-16 rounded object-cover border shrink-0"
                data-testid="img-manual-edit-preview"
              />
            )}
            <div className="min-w-0 flex-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                data-testid="button-replace-image"
              >
                <ImagePlus className="h-4 w-4 mr-1.5" />
                {file ? "Choose a different image" : "Replace image"}
              </Button>
              {file && (
                <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                  <span className="truncate">{file.name}</span>
                  <button
                    type="button"
                    className="shrink-0 hover:text-foreground"
                    onClick={() => {
                      setFile(null);
                      if (previewUrl) URL.revokeObjectURL(previewUrl);
                      setPreviewUrl(null);
                    }}
                    disabled={saving}
                    aria-label="Cancel image replacement"
                    data-testid="button-cancel-replace"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </p>
              )}
              <p className="text-[11px] text-muted-foreground mt-0.5">
                The current image stays recoverable after replacement.
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f && f.type.startsWith("image/")) {
                  if (previewUrl) URL.revokeObjectURL(previewUrl);
                  setFile(f);
                  setPreviewUrl(URL.createObjectURL(f));
                }
                e.target.value = "";
              }}
              data-testid="input-replace-image"
            />
          </div>

          {/* Tags */}
          <div className="grid grid-cols-1 gap-2">
            <PickerSelect
              icon={<Users className="h-4 w-4" />}
              label="Persona"
              placeholder="No persona"
              value={personaId}
              onChange={setPersonaId}
              options={personaOptions}
              testId="select-edit-persona"
              searchable
              optional
              onClear={() => setPersonaId("")}
              disabled={saving}
            />
            <div
              className="flex w-fit items-center gap-1 justify-self-end rounded-full border border-border/70 bg-background/60 p-0.5 shadow-sm"
              data-testid="toggle-edit-promoted-item"
            >
              <Button
                type="button"
                size="sm"
                variant={promotedItemType === "product" ? "default" : "ghost"}
                className="h-7 gap-1 rounded-full px-3 text-[11px]"
                onClick={() => {
                  setPromotedItemType("product");
                  setOfferId("");
                  setAngleId("");
                }}
                disabled={saving}
                data-testid="edit-mode-product"
              >
                <Package className="h-3 w-3" /> Product
              </Button>
              <Button
                type="button"
                size="sm"
                variant={promotedItemType === "offer" ? "default" : "ghost"}
                className="h-7 gap-1 rounded-full px-3 text-[11px]"
                onClick={() => {
                  setPromotedItemType("offer");
                  setProductId("");
                  setAngleId("");
                }}
                disabled={saving}
                data-testid="edit-mode-offer"
              >
                <Sparkles className="h-3 w-3" /> Offer
              </Button>
            </div>
            {promotedItemType === "offer" ? <PickerSelect
              icon={<Package className="h-4 w-4" />}
              label="Offer"
              placeholder="No offer"
              value={offerId}
              onChange={setOfferId}
              options={offerOptions}
              testId="select-edit-offer"
              searchable optional onClear={() => setOfferId("")}
              disabled={saving}
            /> : <PickerSelect
              icon={<Package className="h-4 w-4" />}
              label="Product"
              placeholder="No product"
              value={productId}
              onChange={(v) => {
                setProductId(v);
                if (v && angleId) {
                  const a = angles.find((x) => x.id === angleId);
                  if (a && a.productId !== v) setAngleId("");
                }
              }}
              options={productOptions}
              testId="select-edit-product"
              searchable
              optional
              onClear={() => setProductId("")}
              disabled={saving}
            />}
            <PickerSelect
              icon={<Sparkles className="h-4 w-4" />}
              label="Narrative Angle"
              placeholder={
                visibleAngles.length === 0 ? "No saved angles for this selection" : "No narrative"
              }
              value={angleId}
              onChange={setAngleId}
              groups={angleGroups}
              testId="select-edit-angle"
              searchable
              wrapDescription
              optional
              onClear={() => setAngleId("")}
              disabled={saving || visibleAngles.length === 0}
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={saving} data-testid="button-cancel-manual-edit">
              Cancel
            </Button>
            <Button size="sm" onClick={() => void handleSave()} disabled={saving} data-testid="button-save-manual-edit">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
